import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productsApi, Product } from '@/api/products';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Eye, Pencil, Trash2, ShoppingBag } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function ProductsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsApi.getAll();
      setProducts(data);
    } catch (err) {
      setError('Erreur lors du chargement des produits');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async () => {
    if (!deleteProduct) return;

    try {
      setDeleting(true);
      await productsApi.delete(deleteProduct.code);
      setProducts(products.filter((p) => p.id !== deleteProduct.id));
      setDeleteProduct(null);
    } catch (err: unknown) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(errorMessage);
      setDeleteProduct(null);
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(search)
      );
    }

    result.sort((a, b) => {
      const aValue = a[sortBy as keyof Product];
      const bValue = b[sortBy as keyof Product];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return result;
  }, [products, searchValue, sortBy, sortOrder]);

  const columns: Column<Product>[] = [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      render: (product) => (
        <Link to={`/products/${product.code}`} className="font-medium text-primary hover:underline">
          {product.name}
        </Link>
      ),
    },
    {
      key: 'offerCount',
      header: 'Offres',
      sortable: true,
      className: 'text-center',
      render: (product) => (
        <Badge variant="secondary" className="font-mono">
          {product.offerCount}
        </Badge>
      ),
    },
    {
      key: 'optionCount',
      header: 'Options',
      sortable: true,
      className: 'text-center',
      render: (product) => (
        <Badge variant="outline" className="font-mono">
          {product.optionCount}
        </Badge>
      ),
    },
    {
      key: 'schoolCount',
      header: 'Écoles',
      sortable: true,
      className: 'text-center',
      render: (product) => (
        <Badge variant="outline" className="font-mono">
          {product.schoolCount}
        </Badge>
      ),
    },
    {
      key: 'isActive',
      header: 'Statut',
      sortable: true,
      className: 'text-center',
      render: (product) => (
        <Badge variant={product.isActive ? 'default' : 'secondary'}>
          {product.isActive ? 'Actif' : 'Inactif'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-center w-[130px]',
      render: (product) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/products/${product.code}`)}
            title="Voir"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/products/${product.code}/edit`)}
            title="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteProduct(product)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Produits
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gerez les produits commerciaux et leurs offres
          </p>
        </div>

        {isAdmin && (
          <Button asChild>
            <Link to="/products/new">
              <Plus className="h-4 w-4" />
              Nouveau produit
            </Link>
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredAndSortedProducts}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Rechercher par nom..."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        totalItems={filteredAndSortedProducts.length}
        emptyMessage="Aucun produit trouve"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer le produit{' '}
              <strong>{deleteProduct?.name}</strong> ?
              <br />
              <br />
              Cette action supprimera egalement toutes les offres et options associees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
